import { ref } from 'vue';
import { singleton } from '#shared/singleton';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timeout?: number;
}

export const useToaster = singleton(() => {
  const toastMs = useRuntimeConfig().public.toastMs as number;
  const toasts = ref<Toast[]>([]);
  let nextId = 0;

  const showToast = (message: string, type: Toast['type'] = 'info', timeout?: number) => {
    const finalTimeout = timeout ?? toastMs;
    const id = nextId++;
    const toast: Toast = { id, message, type, timeout };

    toasts.value.push(toast);

    if (finalTimeout) {
      setTimeout(() => {
        removeToast(id);
      }, finalTimeout);
    }

    return id;
  };

  const removeToast = (id: number) => {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  };

  const error = (message: string, timeout?: number) => {
    return showToast(message, 'error', timeout);
  };

  const success = (message: string, timeout?: number) => {
    return showToast(message, 'success', timeout);
  };

  const info = (message: string, timeout?: number) => {
    return showToast(message, 'info', timeout);
  };

  const warn = (message: string, timeout?: number) => {
    return showToast(message, 'warning', timeout);
  };

  return {
    toasts,
    showToast,
    error,
    success,
    info,
    warn,
    removeToast,
    __private: exposeToTests({ 
      get nextId() { return nextId; },
      set nextId(value) { nextId = value; }
    })
  };
});
